import axios, { isAxiosError, type AxiosResponse } from 'axios';
import {
  FulfilmentPicPurpose,
  resolvePicPurposes,
  type FulfilmentPicConfig,
  type FulfilmentPicProfile,
  type VersionedFulfilmentPicConfig,
} from '../models/FulfilmentPicConfig';

const REQUEST_TIMEOUT_MS = 10_000;
const UNKNOWN_PURPOSES_MESSAGE = 'property purposes should not exist';

export const UNSUPPORTED_PURPOSES_ERROR =
  'The server does not support multiple purposes or Gig hand-back yet. Give each PIC at most one purchase purpose, or save again after the Facade update.';

/** Writes `purposes` plus the legacy `purpose` (= purposes[0], omitted when empty) so older readers keep one purpose. */
export const toSavedPic = (pic: FulfilmentPicProfile): FulfilmentPicProfile => {
  const purposes = resolvePicPurposes(pic);
  const savedPic: FulfilmentPicProfile = { ...pic, purposes };
  delete savedPic.purpose;
  const [legacyPurpose] = purposes;
  return legacyPurpose ? { ...savedPic, purpose: legacyPurpose } : savedPic;
};

const toLegacyPic = (pic: FulfilmentPicProfile): FulfilmentPicProfile => {
  const legacyPic = { ...pic };
  delete legacyPic.purposes;
  return legacyPic;
};

const fitsLegacyPurpose = (pic: FulfilmentPicProfile): boolean => {
  const purposes = resolvePicPurposes(pic);
  return purposes.length <= 1 && !purposes.includes(FulfilmentPicPurpose.GIG_HANDBACK);
};

// Older Facade versions whitelist PIC fields and reject `purposes` as an unknown property.
const isUnknownPurposesRejection = (error: unknown): boolean => {
  if (!isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }
  const message: unknown = (error.response.data as { message?: unknown } | undefined)?.message;
  const messages = Array.isArray(message) ? message : [message];
  return messages.some((item) => typeof item === 'string' && item.includes(UNKNOWN_PURPOSES_MESSAGE));
};

export class FulfilmentPicClient {
  private readonly headers: Record<string, string>;

  constructor(
    private readonly apiUrl: string,
    apiKey: string,
  ) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-API-Key': apiKey,
    };
  }

  public async getConfig(): Promise<VersionedFulfilmentPicConfig> {
    const response: AxiosResponse<VersionedFulfilmentPicConfig> = await axios.get(
      `${this.apiUrl}/fulfilment/picConfig`,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }

  /** Saves the config; against a Facade without `purposes` support it retries with legacy single purposes when they fit. */
  public async saveConfig(config: FulfilmentPicConfig): Promise<VersionedFulfilmentPicConfig> {
    const payload: FulfilmentPicConfig = { ...config, pics: config.pics.map(toSavedPic) };
    try {
      return await this.putConfig(payload);
    } catch (error) {
      if (!isUnknownPurposesRejection(error)) {
        throw error;
      }
      if (!payload.pics.every(fitsLegacyPurpose)) {
        throw new Error(UNSUPPORTED_PURPOSES_ERROR);
      }
      return this.putConfig({ ...payload, pics: payload.pics.map(toLegacyPic) });
    }
  }

  private async putConfig(config: FulfilmentPicConfig): Promise<VersionedFulfilmentPicConfig> {
    const response: AxiosResponse<VersionedFulfilmentPicConfig> = await axios.put(
      `${this.apiUrl}/fulfilment/picConfig`,
      config,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }
}
